/**
 * KnowledgeBase — 跨境知识库管理
 *
 * RAG 知识库：知识条目管理、搜索过滤、统计展示
 * 风格: 暗夜蓝 + 萤火暖金
 */

import { useState, useEffect, useCallback } from 'react';
import { Input, Select, Button, Empty, Statistic, Row, Col, message } from 'antd';
import {
  SearchOutlined,
  BookOutlined,
  GlobalOutlined,
  RobotOutlined,
  AimOutlined,
  ImportOutlined,
} from '@ant-design/icons';
import { knowledgeAPI, KNOWLEDGE_TYPES, COUNTRIES } from '../../api/knowledge';
import KnowledgeCard from '../../components/KnowledgeCard';

export default function KnowledgeBase() {
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({ totalDocs: 0, countryCount: 0, weeklyRefs: 0, hitRate: 0 });
  const [keyword, setKeyword] = useState('');
  const [filterType, setFilterType] = useState(undefined);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [docs, statData] = await Promise.all([
        knowledgeAPI.getDocuments({ keyword, type: filterType }),
        knowledgeAPI.getStats(),
      ]);
      setDocuments(docs);
      setStats(statData);
    } catch {
      message.error('加载知识库失败');
    } finally {
      setLoading(false);
    }
  }, [keyword, filterType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async (id) => {
    try {
      await knowledgeAPI.deleteDocument(id);
      message.success('文档已删除');
      loadData();
    } catch {
      message.error('删除失败');
    }
  };

  const handleEdit = (doc) => {
    message.info(`编辑: ${doc.title}`);
  };

  const handleImport = () => {
    message.info('导入功能即将上线');
  };

  return (
    <div style={styles.pageShell}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Header */}
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>
            <BookOutlined style={{ marginRight: 12, fontSize: 28 }} />
            RAG 跨境知识库
          </h1>
          <p style={styles.subtitle}>
            为 Agent 提供结构化跨境知识，赋能智能决策与自动执行
          </p>
        </div>
      </header>

      {/* Stats Row */}
      <Row gutter={16} style={styles.statsRow}>
        <Col span={6}>
          <div style={{ ...styles.statCard, borderColor: 'rgba(168,199,216,0.2)' }}>
            <BookOutlined style={{ fontSize: 20, color: '#A8C7D8', marginBottom: 8 }} />
            <Statistic
              title={<span style={styles.statLabel}>知识条目总数</span>}
              value={stats.totalDocs}
              valueStyle={{ color: '#A8C7D8', fontSize: 28, fontWeight: 700 }}
            />
          </div>
        </Col>
        <Col span={6}>
          <div style={{ ...styles.statCard, borderColor: 'rgba(232,210,138,0.2)' }}>
            <GlobalOutlined style={{ fontSize: 20, color: '#E8D28A', marginBottom: 8 }} />
            <Statistic
              title={<span style={styles.statLabel}>覆盖国家数</span>}
              value={stats.countryCount}
              valueStyle={{ color: '#E8D28A', fontSize: 28, fontWeight: 700 }}
            />
          </div>
        </Col>
        <Col span={6}>
          <div style={{ ...styles.statCard, borderColor: 'rgba(129,140,248,0.2)' }}>
            <RobotOutlined style={{ fontSize: 20, color: '#818cf8', marginBottom: 8 }} />
            <Statistic
              title={<span style={styles.statLabel}>本周Agent引用</span>}
              value={stats.weeklyRefs}
              valueStyle={{ color: '#818cf8', fontSize: 28, fontWeight: 700 }}
            />
          </div>
        </Col>
        <Col span={6}>
          <div style={{ ...styles.statCard, borderColor: 'rgba(52,211,153,0.2)' }}>
            <AimOutlined style={{ fontSize: 20, color: '#34d399', marginBottom: 8 }} />
            <Statistic
              title={<span style={styles.statLabel}>命中率</span>}
              value={stats.hitRate}
              suffix="%"
              valueStyle={{ color: '#34d399', fontSize: 28, fontWeight: 700 }}
            />
          </div>
        </Col>
      </Row>

      {/* Search & Filter Bar */}
      <div style={styles.toolbar}>
        <Input.Search
          placeholder="搜索知识文档标题或简介..."
          allowClear
          enterButton={<SearchOutlined />}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onSearch={() => loadData()}
          style={{ maxWidth: 360 }}
        />
        <Select
          placeholder="知识类型"
          allowClear
          value={filterType}
          onChange={setFilterType}
          style={{ width: 160 }}
          options={Object.entries(KNOWLEDGE_TYPES).map(([key, val]) => ({
            label: val.label,
            value: key,
          }))}
          popupMatchSelectWidth={false}
        />
        <Button
          type="primary"
          icon={<ImportOutlined />}
          onClick={handleImport}
          style={{
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            border: '1px solid rgba(139,92,246,0.3)',
            borderRadius: 8,
            fontWeight: 500,
          }}
        >
          导入文档
        </Button>
      </div>

      {/* Knowledge Cards Grid */}
      {documents.length > 0 ? (
        <Row gutter={[16, 16]}>
          {documents.map((doc) => (
            <Col span={8} key={doc.id}>
              <KnowledgeCard
                document={doc}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            </Col>
          ))}
        </Row>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: '80px 0', color: 'rgba(255,255,255,0.4)' }}>
          加载中...
        </div>
      ) : (
        <div style={{ padding: '80px 0' }}>
          <Empty
            description={
              <span style={{ color: 'rgba(255,255,255,0.4)' }}>
                暂无匹配的知识文档
              </span>
            }
          />
        </div>
      )}
    </div>
  );
}

// Styles
const styles = {
  pageShell: {
    minHeight: '100vh',
    padding: '32px 36px 80px',
    fontFamily: "'Inter', -apple-system, sans-serif",
    color: '#f0f5fa',
  },
  header: {
    marginBottom: 28,
    animation: 'fadeUp 500ms ease both',
  },
  title: {
    fontSize: 26,
    fontWeight: 600,
    color: '#f0f5fa',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  subtitle: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.45)',
    marginTop: 6,
  },
  statsRow: {
    marginBottom: 24,
    animation: 'fadeUp 500ms 100ms ease both',
  },
  statCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px 16px',
    background: 'rgba(255,255,255,0.04)',
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.08)',
  },
  statLabel: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 12,
  },
  toolbar: {
    display: 'flex',
    gap: 12,
    marginBottom: 24,
    alignItems: 'center',
    flexWrap: 'wrap',
    animation: 'fadeUp 500ms 200ms ease both',
  },
};
